/* eslint-disable react-hooks/exhaustive-deps */
import _ from 'lodash'
import moment from 'moment'
import { navigate } from 'gatsby'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Helmet } from 'react-helmet'
import {
  useAuth,
  useBrands,
  useCart,
  useCategories,
  useCurrencies,
  useDepartments,
  useDineInCart,
  useMetaDefinitions,
  useStores,
  useSystemSettings,
  useUser,
  useOrderMethod,
} from 'react-omnitech-api'
import ReactGA from 'react-ga';
import {
  AlertProvider,
  CommerceChannelProvider,
  CouponMarketplaceProvider,
  DineInMiniCartProvider,
  GoogleMapsProvider,
  LocationProvider,
  LinkProvider,
  MenuProvider,
  MiniCartProvider,
  MobileMenuProvider,
  ModalProvider,
  OrderMethodModalProvider,
  ProductQuickAddModalProvider,
  CompareProvider,
  ThemeConfigProvider,
  FavouritesProvider,
  usePrevious,
  // useOrderMethod, // useOrderMethod in hook dose not work here
} from '../../hook'
import Loading from '../app-loading'
import ThemeProvider from '../theme-provider'
import appVersion from '../../helpers/app-version'

/**
 * Application
 * Component to handle app init, preload required data from API
 * @param param0
 */
export default function Application({
  pageContext = {},
  config,
  children,
  location,
}) {
  const { locale } = pageContext
  const [ready, setReady] = useState(false)
  const [theme, setTheme] = useState({})
  // preload required data before app ready
  const { initCartForCartBadge } = useCart()
  const { initCartForCartBadge: initDineInCart } = useDineInCart()
  const { brandsReady } = useBrands()
  const { categoriesReady } = useCategories()
  const { currenciesReady } = useCurrencies()
  const { departmentsReady } = useDepartments()
  const { metaDefinitionsReady } = useMetaDefinitions()
  const { getSystemSetting, systemSettingsReady } = useSystemSettings()
  const { auth } = useAuth()
  const { userReady } = useUser()
  const {
    clearOrderMethod,
    orderMethod,
    setStore,
    store,
  } = useOrderMethod()
  const { fetchStore, stores } = useStores()
  const trackingCode = getSystemSetting('analytics.google.tracking_code.ecom')
  const trackingGTMCode = getSystemSetting('analytics.google.tag_manager_code.ecom')
  const fnbEnabled = getSystemSetting('features.fnb.enable')

  // ready when required data is loaded
  const isReady = useMemo(
    () => (
      !_.includes([
        brandsReady, categoriesReady, currenciesReady,
        departmentsReady, systemSettingsReady, userReady,
        metaDefinitionsReady,
      ], false)
    ),
    [
      brandsReady, categoriesReady, currenciesReady,
      departmentsReady, systemSettingsReady, userReady,
      metaDefinitionsReady,
    ],
  )

  const orderMethodCode = useMemo(() => _.get(orderMethod, 'code'), [orderMethod])
  const getThemeConfig = useCallback(() => {
    const themeConfig = getSystemSetting('theme.ecom.config', {})
    setTheme(themeConfig)
  }, [getSystemSetting, setTheme])

  const updateStore = async (id) => {
    try {
      const includes = [
        'meta',
        'today_open_time_slots',
      ]
      const { store: updatedStore } = await fetchStore({ id, includes })
      // update store in localstorage (ensure only save store.id and store.code)
      setStore(_.pick(updatedStore, ['id', 'code']))
    } catch (error) {
      // do nothing
    }
  }

  const initCart = () => {
    const selectedOrderMethodCode = _.get(orderMethod, 'code')
    const selectableOrderMethod = _.find(
      _.get(theme, 'config.availableOrderMethod', []),
      { code: selectedOrderMethodCode },
    )
    if (_.get(selectableOrderMethod, 'commerceType') === 'dineIn') {
      // FL: Only fetch dine-in cart in dine-in mode
      if (_.get(selectableOrderMethod, 'code') === 'dineIn') {
        // fetch dineInCart
        initDineInCart({
          includes: [
            'await_confirm_order',
            'physical_store_id',
            'carts.meta',
            'cart_shipments',
            'cart_shipments.inventory_store',
          ],
        })
          .catch(() => {
            // Nav to dine-in and show Dine-in completed message
            navigate(`/${locale}/dine-in/`)
          })
      }
      return
    }
    // check store is update to date in local storage
    const storeCode = _.get(store, 'code')
    const storeOpenTimeStart = _.get(_.find(stores, { code: storeCode }), 'todayOpenTimeSlots.0.start')

    if (
      !_.isEmpty(storeOpenTimeStart)
      && !moment(storeOpenTimeStart || '1900-01-01').isSame(new Date(), 'day')
    ) {
      updateStore(_.get(store, 'id'))
    }

    initCartForCartBadge({
      includes: [
        'await_confirm_order',
        'cart_shipments',
        'cart_shipments.inventory_store',
        'cart_line_properties.sku_id',
        // FL: Add Includes pickup store for store name on order method button
        ...(fnbEnabled ? [
          'cart_shipments.delivery_address',
          'cart_shipments.pickup_store',
          'available_delivery_types',
        ] : []),
      ],
    })
  }

  useEffect(() => {
    if (!ready) return
    // Do nothing when scanning NFC
    if (/\/dine-in\/$/.test(_.get(location, 'pathname', ''))) return
    initCart()
  }, [ready])

  // trigger initCart again after user logged out
  const authToken = useMemo(() => _.get(auth, 'authToken'), [auth])
  const prevAuthToken = usePrevious(authToken)
  useEffect(() => {
    if (
      _.isEmpty(authToken)
      && prevAuthToken !== authToken
      && !_.isUndefined(prevAuthToken)
    ) {
      initCart()
    }
  }, [authToken])

  // get theme config from system setting after it is ready
  useEffect(() => {
    if (!systemSettingsReady) return
    getThemeConfig()
  }, [getThemeConfig, systemSettingsReady])

  // load required data required by Omnitech
  useEffect(() => {
    if (isReady && !ready) {
      setReady(isReady)
    }
  }, [isReady, ready])

  useEffect(() => {
    if (!_.isEmpty(trackingCode)) {
      ReactGA.initialize(trackingCode);
      ReactGA.pageview(window.location.pathname + window.location.search)
      ReactGA.plugin.require('ec')
    }
  }, [trackingCode, trackingGTMCode])

  // clear orderMethod when
  //  - orderMethodSchemaVersion is exists
  //  - and orderMethodSchemaVersion is more updated then stored schemaVersion in order method
  useEffect(() => {
    const storedOrderMethodSchemaVersion = _.get(orderMethod, 'schemaVersion')
    if (
      // start when theme config is ready
      _.has(theme, 'config')
    ) {
      const orderMethodSchemaVersion = _.get(theme, 'config.orderMethodSchemaVersion')
      if (
        !_.isEmpty(orderMethodSchemaVersion)
        // One of orderMethod is already selected by user
        && !_.isEmpty(orderMethod)
        && (
          _.isEmpty(storedOrderMethodSchemaVersion)
          || appVersion(storedOrderMethodSchemaVersion).isBefore(orderMethodSchemaVersion)
        )
      ) {
        clearOrderMethod()
      }
    }
  }, [orderMethod, theme])

  return ready
    ? (
      <ThemeProvider theme={theme}>
        {/*
          // Using Link hook to support navigate with locale prepended in link
        */}
        <LinkProvider value={{ locale }}>
          <LocationProvider value={{ location, locale }}>
            <ThemeConfigProvider>
              <CommerceChannelProvider value={{ location, locale }}>
                <MenuProvider>
                  <AlertProvider>
                    <ModalProvider>
                      <OrderMethodModalProvider>
                        <FavouritesProvider>
                          <ProductQuickAddModalProvider>
                            <DineInMiniCartProvider>
                              <MiniCartProvider>
                                <MobileMenuProvider>
                                  <CouponMarketplaceProvider>
                                    <CompareProvider>
                                      <GoogleMapsProvider apiKey={_.get(config, 'service.googleMapsApiKey')}>
                                        <Helmet htmlAttributes={{ 'data-order-method': orderMethodCode || 'default' }} />
                                        {children}
                                      </GoogleMapsProvider>
                                    </CompareProvider>
                                  </CouponMarketplaceProvider>
                                </MobileMenuProvider>
                              </MiniCartProvider>
                            </DineInMiniCartProvider>
                          </ProductQuickAddModalProvider>
                        </FavouritesProvider>
                      </OrderMethodModalProvider>
                    </ModalProvider>
                  </AlertProvider>
                </MenuProvider>
              </CommerceChannelProvider>
            </ThemeConfigProvider>
          </LocationProvider>
        </LinkProvider>
      </ThemeProvider>
    )
    : <Loading />
}
